import { useEffect, useState } from 'react';
import ArchitectureEditor from './pages/ArchitectureEditor';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import OrganizationPage from './pages/OrganizationPage';
import WorkspaceProjectPage from './pages/WorkspaceProjectPage';
import AuthPage from './pages/AuthPage';
import { useAuthStore } from './store/useAuthStore';
import { useWorkspaceStore } from './store/useWorkspaceStore';

type Route =
  | { page: 'dashboard' }
  | { page: 'organization'; orgId: string }
  | { page: 'workspace-project'; orgId: string; projectId: string }
  | { page: 'editor'; projectId: string }
  | { page: 'architecture-editor'; projectId: string };

function parseRoute(hash: string): Route {
  const wsProject = hash.match(/^#\/org\/([^/]+)\/project\/([^/]+)$/);
  if (wsProject) return { page: 'workspace-project', orgId: wsProject[1], projectId: wsProject[2] };
  const org = hash.match(/^#\/org\/([^/]+)$/);
  if (org) return { page: 'organization', orgId: org[1] };
  const architectureMatch = hash.match(/^#\/architecture\/(.+)$/);
  if (architectureMatch) return { page: 'architecture-editor', projectId: architectureMatch[1] };
  const match = hash.match(/^#\/project\/(.+)$/);
  if (match) return { page: 'editor', projectId: match[1] };
  return { page: 'dashboard' };
}

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return <div className="flex h-screen items-center justify-center bg-canvas text-sm text-gray-400 dark:bg-gray-950 dark:text-gray-500">{children}</div>;
}

export default function App() {
  const status = useAuthStore((s) => s.status);
  const checkSession = useAuthStore((s) => s.checkSession);
  const load = useWorkspaceStore((s) => s.load);
  const reset = useWorkspaceStore((s) => s.reset);
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    checkSession();
    const onHashChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [checkSession]);

  // Everything the user can see comes from the server once they are signed in; signing out clears it.
  useEffect(() => {
    if (status === 'authenticated') {
      setLoadError(null);
      load().catch((err: Error) => setLoadError(err.message));
    } else if (status === 'anonymous') {
      reset();
    }
  }, [status, load, reset]);

  if (status === 'loading') return <FullScreenMessage>Loading…</FullScreenMessage>;
  if (status === 'anonymous') return <AuthPage />;
  if (loadError) {
    return (
      <FullScreenMessage>
        <div className="text-center">
          <p className="mb-3">{loadError}</p>
          <button className="btn-primary" onClick={() => load().then(() => setLoadError(null)).catch((e: Error) => setLoadError(e.message))}>
            Try again
          </button>
        </div>
      </FullScreenMessage>
    );
  }
  if (!hydrated) return <FullScreenMessage>Loading your workspace…</FullScreenMessage>;

  switch (route.page) {
    case 'editor':
      return <Editor projectId={route.projectId} />;
    case 'architecture-editor':
      return <ArchitectureEditor projectId={route.projectId} />;
    case 'organization':
      return <OrganizationPage orgId={route.orgId} />;
    case 'workspace-project':
      return <WorkspaceProjectPage orgId={route.orgId} projectId={route.projectId} />;
    default:
      return <Dashboard />;
  }
}

export function navigateToProject(id: string) {
  window.location.hash = `#/project/${id}`;
}

export function navigateToArchitectureProject(id: string) {
  window.location.hash = `#/architecture/${id}`;
}

export function navigateToOrganization(orgId: string) {
  window.location.hash = `#/org/${orgId}`;
}

export function navigateToWorkspaceProject(orgId: string, projectId: string) {
  window.location.hash = `#/org/${orgId}/project/${projectId}`;
}

/** Goes "up" one level: from a diagram editor back to its project, otherwise to the organizations list. */
export function navigateToDashboard() {
  const route = parseRoute(window.location.hash);
  const { findProjectOfDiagram } = useWorkspaceStore.getState();
  const owner =
    route.page === 'editor'
      ? findProjectOfDiagram('er', route.projectId)
      : route.page === 'architecture-editor'
        ? findProjectOfDiagram('architecture', route.projectId)
        : undefined;
  window.location.hash = owner ? `#/org/${owner.orgId}/project/${owner.id}` : '#/';
}
